import { useCallback } from "react"
import type { Row, Table } from "@tanstack/react-table"
import { utils, writeFile, SSF } from "xlsx"
import { format } from "date-fns"
import { parseDateOnly } from "@/lib/utils"
import { computeGroupTotals, currencyFieldOf, groupDisplayLabel, isAdditiveColumn } from "@/lib/group-totals"

// Excel number-format codes applied per column based on meta.type.
// Currency is intentionally currency-agnostic (no "$"): some reports
// (e.g. Time Entries) mix currencies per row, so a hardcoded symbol would
// mislabel non-USD rows. "@" is Excel's built-in Text format so text-like
// columns are stored (and left) as text rather than General.
const EXCEL_FORMATS: Record<string, string> = {
  // "yyyy-mm-dd;@" is the exact code of Excel's built-in "2012-03-14" Date
  // entry (the trailing ";@" text section is what makes Excel match it to the
  // Date category rather than falling back to "Custom"). Plain "yyyy-mm-dd"
  // renders identically but is treated as a custom format by Excel.
  date: "yyyy-mm-dd;@",
  currency: "#,##0.00",
  number: "#,##0.00",
  integer: "#,##0",
  percent: "0.0%",
  string: "@",
  status: "@",
  boolean: "@",
}

// "#,##0.00", "#,##0" and "@" resolve to Excel built-in format ids (4, 3, 49),
// so Excel files them under Number/Text automatically. "yyyy-mm-dd;@" and
// "0.0%" have no built-in id: SheetJS would otherwise append them in the
// reserved (<164) id range, which Excel shows under "Custom" even though they
// render correctly. Registering them at spec-compliant custom ids (>=164)
// makes Excel match the code to its Date / Percentage samples (e.g. the
// "2012-03-14" date format) and categorise the cells accordingly.
SSF.load("yyyy-mm-dd;@", 164)
SSF.load("0.0%", 165)

export function useReportExport<T>(table: Table<T> | null, reportName: string) {
  const exportToExcel = useCallback(() => {
    if (!table) return

    const shownColumns = table.getVisibleFlatColumns()

    // The grid puts the currency in the cell, as a symbol. The spreadsheet cannot: its money format
    // is deliberately symbol-free, because a hardcoded symbol would mislabel every row not in that
    // currency. So the only place a denomination can live in the file is a currency column - and
    // those are hidden in the grid by default, which means exporting the visible columns alone
    // produces a sheet of amounts denominated in nothing at all.
    //
    // Derived from what the visible money columns actually name, rather than a hardcoded list of
    // ids, so a report adding a third currency field gets carried without touching this.
    const shownIds = new Set(shownColumns.map((col) => col.id))
    const qualifierIds = new Set(
      shownColumns
        .filter((col) => (col.columnDef.meta as any)?.type === "currency")
        .map((col) => currencyFieldOf(col.columnDef.meta))
    )
    const qualifierColumns = table
      .getAllLeafColumns()
      .filter((col) => qualifierIds.has(col.id) && !shownIds.has(col.id))

    const exportColumns = [...shownColumns, ...qualifierColumns]

    // Build header row
    const headers = exportColumns.map((col) => {
      const header = col.columnDef.header
      return typeof header === "string" ? header : col.id
    })

    // Per-column meta.type — drives both value coercion below and the Excel
    // number format stamped on the cells afterwards.
    const columnTypes = exportColumns.map(
      (col) => (col.columnDef.meta as any)?.type as string | undefined
    )

    // Coerce one body value to what Excel should store: real Date / number, so
    // Excel applies date and numeric formats instead of treating every cell as
    // General text.
    const toCell = (value: unknown, type: string | undefined) => {
      if (value == null || value === "") return ""
      switch (type) {
        case "date":
          // Reuse parseDateOnly (not new Date(str)) to avoid the UTC-midnight
          // off-by-one-day shift in negative-offset timezones.
          return parseDateOnly(String(value)) ?? String(value)
        case "percent":
          // Stored values are already scaled ×100 (e.g. 12.5 = 12.5%). Divide
          // by 100 so the "0.0%" format renders correctly and the cell is a
          // true Excel percentage that behaves in downstream formulas.
          return Number(value) / 100
        case "currency":
        case "number":
        case "integer":
          return Number(value)
        case "boolean":
          return value ? "Yes" : "No"
        default:
          return value
      }
    }

    const dataRowCells = (row: Row<T>) =>
      exportColumns.map((col, c) => toCell(row.getValue(col.id), columnTypes[c]))

    // Where a group row puts its name: the first column with nothing of its own
    // to show, mirroring the grid's totals row. Columns are reorderable and
    // hideable, so a fixed index would overwrite a real figure.
    const labelColumnIdx = exportColumns.findIndex((c) => {
      const m = (c.columnDef.meta as any) ?? {}
      return !isAdditiveColumn(m) && !m.groupTotalRatio
    })

    // A group becomes ONE row carrying both its name and its totals, with its
    // members nested beneath it. Collapsed, the sheet reads as the summary the
    // grid shows collapsed; expanded, the rows behind each figure are right
    // there. A separate totals row would either vanish with the members when
    // collapsed or float free of the group it belongs to.
    const groupRowCells = (group: Row<T>) => {
      // No currency needed: the spreadsheet's money format carries no symbol.
      const { values, mixedCurrency } = computeGroupTotals(
        group,
        table.getAllLeafColumns()
      )
      // Named and counted exactly as the grid's group header does it: the shared label handles the
      // no-value bucket ("(No Branch)"), and the count is of the rows directly beneath - which at
      // two grouping levels is a count of sub-groups, not of every data row under them.
      const label = groupDisplayLabel(
        group.groupingValue,
        table.getColumn(group.groupingColumnId ?? "")?.columnDef.header
      )

      return exportColumns.map((col, c) => {
        if (c === labelColumnIdx) return `${label} (${group.subRows.length})`
        const total = values.get(col.id)
        // Summing across currencies and stamping one symbol on the result would
        // be wrong, so say so rather than emit a number. Per column: a report
        // carrying a bill and a pay currency can mix one and not the other.
        if (mixedCurrency.has(col.id)) {
          return "Mixed currency"
        }
        return total == null ? "" : toCell(total, columnTypes[c])
      })
    }

    // Outline level per body row, so Excel gets real collapsible groups. Flat
    // when nothing is grouped, in which case every row sits at level 0 and the
    // file is exactly what it was before.
    const data: any[][] = []
    const levels: number[] = []

    const walk = (rows: Row<T>[], level: number) => {
      for (const row of rows) {
        if (row.getIsGrouped()) {
          data.push(groupRowCells(row))
          levels.push(level)
          walk(row.subRows, level + 1)
        } else {
          data.push(dataRowCells(row))
          levels.push(level)
        }
      }
    }

    const isGrouped = table.getState().grouping.length > 0
    // The sorted model sits after grouping in TanStack's pipeline, so it is the
    // grouped tree with subRows sorted too - i.e. the rows in the order the
    // screen shows them. (Grouping aside, this also means the file now follows
    // the user's sort, which the previous filtered-model export did not.)
    walk(table.getSortedRowModel().rows, 0)

    // Create worksheet. Date values become numeric date serials with a default
    // date format, which we override per column below.
    const ws = utils.aoa_to_sheet([headers, ...data])

    if (isGrouped) {
      // Row 0 is the header, outside the outline. Children start hidden so the
      // file opens at the summary level, the way the grid opens collapsed.
      ws["!rows"] = [{}, ...levels.map((level) => ({ level, hidden: level > 0 }))]
      // Excel's default puts the +/- toggle on the row AFTER a group, which
      // here is the next group's own row. Ours summarise from above.
      ws["!outline"] = { above: true }
    }

    // Stamp the Excel number format on each body cell whose column has one.
    // Header is row 0; data rows start at row 1.
    columnTypes.forEach((type, c) => {
      const fmt = type ? EXCEL_FORMATS[type] : undefined
      if (!fmt) return
      const isText = fmt === "@"
      for (let i = 0; i < data.length; i++) {
        const cell = ws[utils.encode_cell({ r: i + 1, c })]
        if (!cell) continue
        // Text format applies to string cells; numeric formats apply to real
        // number/date cells. Never cross the two (e.g. a "@" on a number would
        // suppress its value), and leave blanks untouched.
        if (isText) {
          if (cell.t === "s") cell.z = fmt
        } else if (cell.t === "n" || cell.t === "d") {
          cell.z = fmt
        }
      }
    })

    // Set column widths based on header lengths
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 2, 12) }))

    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, reportName.slice(0, 31))

    const filename = `${reportName.replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}.xlsx`
    writeFile(wb, filename)
  }, [table, reportName])

  return { exportToExcel }
}
